import {
  promoteCountForGroup,
  relegateCountForGroup,
  type Movement
} from '@h2h/shared';
import { ordinalSuffix } from './format';
import { zoneForRank } from './round';

/** Plain-English copy explaining *why* a player moved (or didn't) in their division. */
export function movementVerdictCopy({
  rank,
  divisionSize,
  movement,
  promote: promoteOverride
}: {
  rank: number;
  divisionSize: number;
  movement?: Movement | null;
  promote?: number;
}): { rankLine: string; why: string } {
  const promote = promoteOverride ?? promoteCountForGroup(Math.max(divisionSize, rank));
  const relegate = relegateCountForGroup(Math.max(divisionSize, rank));
  const rankLabel = `${rank}${ordinalSuffix(rank)}`;
  const rankLine = divisionSize > 0 ? `${rankLabel} of ${divisionSize}` : rankLabel;

  if (movement === 'PROMOTED') {
    return {
      rankLine,
      why: promote > 0 ? `Top ${promote} promote — you finished in the zone` : 'Finished in the promotion zone'
    };
  }
  if (movement === 'RELEGATED') {
    return {
      rankLine,
      why: relegate > 0 ? `Bottom ${relegate} drop — you finished in the zone` : 'Finished in the drop zone'
    };
  }
  if (movement === 'STAYED') {
    if (rank === 1) {
      return { rankLine, why: 'Won your group — held your tier' };
    }
    const zone = zoneForRank(rank, Math.max(divisionSize, rank));
    if (zone === 'middle' && promote > 0 && rank > promote) {
      const gap = rank - promote;
      return {
        rankLine,
        why: gap === 1 ? '1 spot off promotion — held your tier' : `${gap} spots off promotion — held your tier`
      };
    }
    return { rankLine, why: 'Mid-table — held your tier' };
  }

  const zone = zoneForRank(rank, Math.max(divisionSize, rank));
  if (zone === 'promotion') {
    return {
      rankLine,
      why: promote > 0 ? `Promotion zone · top ${promote} go up when the round ends` : 'Promotion zone'
    };
  }
  if (zone === 'relegation') {
    const safety = Math.max(divisionSize, rank) - relegate;
    const gap = Math.max(1, rank - safety);
    return {
      rankLine,
      why: `Drop zone · ${gap} ${gap === 1 ? 'place' : 'places'} below safety`
    };
  }
  if (promote > 0 && rank > promote) {
    const gap = rank - promote;
    return {
      rankLine,
      why: gap === 1 ? '1 spot off promotion' : `${gap} spots off promotion`
    };
  }
  return { rankLine, why: 'Holding mid-table' };
}

/** Short stakes reminder for division headers. */
export function divisionStakesLine(divisionSize: number, tierAtBottom: boolean): string {
  const promote = promoteCountForGroup(divisionSize);
  const relegate = tierAtBottom ? 0 : relegateCountForGroup(divisionSize);
  const parts: string[] = [];
  if (promote > 0) parts.push(`top ${promote} promote`);
  if (relegate > 0) parts.push(`bottom ${relegate} drop`);
  if (parts.length === 0) return 'Group still filling — promotion bands appear as rivals join';
  return parts.join(' · ');
}
